/* ============================================================
   Top Knobs — Typesense-powered header search autocomplete.
   Replaces Shopify's native predictive-search dropdown.

   On every header search input (desktop + mobile), debounces
   typing → multi-query to Algolia (products + collections in
   one round-trip) → renders a custom 2-column dropdown panel.

   Native Shopify predictive-search panel is hidden via CSS so
   it doesn't appear underneath. The native component still fires
   its /search/suggest.json calls but its UI is invisible.

   On submit (Enter key or magnifier click) the form goes to
   /search?q=... where tk-algolia-search.js takes over the SRP.

   Engine: Typesense (migrated from Algolia 2026-08). Same scoped
   search-only key as tk-typesense-search.js — client-exposed by design.
   Responses are translated to the Algolia result shape renderPanel()
   was written against; render code is UNCHANGED.
   ============================================================ */

(function () {
  'use strict';

  const TS_HOST = 'https://8dcxftm9ab6hl0s4p-1.a2.typesense.net';
  const TS_SEARCH_KEY = 'QKwoYeZyD3Yib4ZOqneWACw2IR8OfYSH';
  const PRODUCTS_INDEX = 'tk_products';
  const COLLECTIONS_INDEX = 'tk_collections';
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 150;
  const MAX_PRODUCTS = 6;
  const MAX_COLLECTIONS = 5;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function ensureStyles() {
    if (document.getElementById('tk-ap-style')) return;
    const style = document.createElement('style');
    style.id = 'tk-ap-style';
    style.textContent = `
      /* Suppress Shopify's native predictive-search panel —
         we own the autocomplete UI now. */
      predictive-search .predictive-search.absolute,
      .js-search-results > .predictive-search.absolute {
        display: none !important;
      }

      .tk-ap-panel {
        position: absolute;
        top: calc(100% + 4px);
        left: 0; right: 0;
        z-index: 60;
        background: var(--color-bg, #fff);
        border: 1px solid var(--color-border, #e0e0e0);
        box-shadow: 0 8px 24px rgba(0,0,0,0.10);
        max-height: min(70vh, 620px);
        overflow-y: auto;
        display: none;
      }
      .tk-ap-panel.is-open { display: block; }

      .tk-ap-cols {
        display: grid;
        grid-template-columns: 1.6fr 1fr;
        gap: 28px;
        padding: 18px 20px 4px;
      }
      .tk-ap-col-h {
        font-family: var(--tk-font-body, sans-serif);
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--color-text2, #6e7177);
        margin: 0 0 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid var(--color-border, #e0e0e0);
      }

      .tk-ap-product {
        display: flex; gap: 12px;
        padding: 8px; margin: 0 -8px;
        text-decoration: none; color: var(--color-text, #1d1d1f);
        align-items: flex-start;
        transition: background 0.12s ease;
      }
      .tk-ap-product:hover,
      .tk-ap-product.is-active { background: var(--color-bg2, #f6f7fa); }
      .tk-ap-product__img {
        width: 56px; height: 56px;
        object-fit: contain; flex-shrink: 0;
        background: var(--color-bg2, #f6f7fa);
      }
      .tk-ap-product__info { min-width: 0; flex: 1; padding-top: 2px; }
      .tk-ap-product__title {
        font-family: var(--tk-font-heading, serif);
        font-size: 14px; font-weight: 400;
        line-height: 1.3;
        color: var(--color-text, #1d1d1f);
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .tk-ap-product__title em {
        font-style: normal; font-weight: 600;
        color: var(--color-accent, #c2a058);
        background: transparent;
      }
      .tk-ap-product__price {
        font-family: var(--tk-font-body, sans-serif);
        font-size: 12px; color: var(--color-text2);
        margin: 4px 0 0;
      }
      .tk-ap-product__price-was {
        text-decoration: line-through;
        opacity: 0.6; margin-left: 6px;
      }

      .tk-ap-collection {
        display: flex; align-items: center; justify-content: space-between;
        gap: 10px;
        padding: 10px 8px; margin: 0 -8px;
        font-family: var(--tk-font-body, sans-serif);
        font-size: 13px;
        color: var(--color-text, #1d1d1f);
        text-decoration: none;
        transition: background 0.12s ease, color 0.12s ease;
      }
      .tk-ap-collection:hover,
      .tk-ap-collection.is-active {
        background: var(--color-bg2);
        color: var(--color-accent);
      }
      .tk-ap-collection em {
        font-style: normal; font-weight: 600;
        color: var(--color-accent, #c2a058);
        background: transparent;
      }
      .tk-ap-collection__count {
        font-family: var(--tk-font-body);
        font-size: 11px;
        color: var(--color-text2);
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
      }

      .tk-ap-viewall {
        display: block;
        margin: 18px 0 0;
        padding: 14px;
        text-align: center;
        background: var(--color-bg2, #f6f7fa);
        font-family: var(--tk-font-body);
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--color-accent, #c2a058);
        text-decoration: none;
        border-top: 1px solid var(--color-border);
        transition: background 0.15s ease, color 0.15s ease;
      }
      .tk-ap-viewall:hover {
        background: var(--color-accent);
        color: #fff;
      }

      .tk-ap-empty {
        font-family: var(--tk-font-heading, serif);
        font-size: 16px; font-weight: 400;
        color: var(--color-text2);
        text-align: center;
        padding: 36px 24px;
      }

      @media (max-width: 600px) {
        .tk-ap-cols { grid-template-columns: 1fr; gap: 16px; }
      }
    `;
    document.head.appendChild(style);
  }

  let abort = null;

  async function doSearch(q, panel) {
    q = (q || '').trim();
    if (q.length < MIN_CHARS) {
      panel.classList.remove('is-open');
      return;
    }

    if (abort) abort.abort();
    abort = new AbortController();

    try {
      const res = await fetch(TS_HOST + '/multi_search', {
        method: 'POST',
        headers: { 'X-TYPESENSE-API-KEY': TS_SEARCH_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          searches: [
            {
              collection: PRODUCTS_INDEX,
              q: q,
              query_by: 'title,sku,vendor,product_type,tags',
              per_page: MAX_PRODUCTS,
              include_fields: 'title,handle,price,compare_at_price,image',
              highlight_full_fields: 'title',
              highlight_start_tag: '<em>',
              highlight_end_tag: '</em>',
              typo_tokens_threshold: 50,
              drop_tokens_threshold: 5
            },
            {
              collection: COLLECTIONS_INDEX,
              q: q,
              query_by: 'title,handle',
              per_page: MAX_COLLECTIONS,
              include_fields: 'title,handle,products_count',
              highlight_full_fields: 'title',
              highlight_start_tag: '<em>',
              highlight_end_tag: '</em>'
            }
          ]
        }),
        signal: abort.signal
      });
      if (!res.ok) throw new Error('Typesense autocomplete failed: ' + res.status);
      const data = await res.json();
      renderPanel(panel, q, tkTranslate(data.results[0]), tkTranslate(data.results[1]));
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[tk-ts-ap]', e);
    }
  }

  function tkTranslate(r) {
    if (!r || r.error) return { hits: [], nbHits: 0 };
    const hits = (r.hits || []).map(h => {
      const d = h.document || {};
      const hit = Object.assign({}, d);
      const o = h.highlight && h.highlight.title;
      const a = (h.highlights || []).find(x => x.field === 'title');
      const hl = (o && (o.value || o.snippet)) || (a && (a.value || a.snippet)) || null;
      hit._highlightResult = hl ? { title: { value: hl } } : null;
      // products_count isn't maintained yet in tk_collections — hide zeros.
      if (hit.products_count === 0) hit.products_count = null;
      return hit;
    });
    return { hits: hits, nbHits: r.found || 0 };
  }

  function renderPanel(panel, q, products, collections) {
    const productsHits = (products && products.hits) || [];
    const collectionsHits = (collections && collections.hits) || [];

    if (productsHits.length === 0 && collectionsHits.length === 0) {
      panel.innerHTML = `<div class="tk-ap-empty">No results for &ldquo;${escapeHtml(q)}&rdquo;</div>`;
      panel.classList.add('is-open');
      return;
    }

    let html = '<div class="tk-ap-cols">';

    if (productsHits.length > 0) {
      html += '<div class="tk-ap-col-products"><h3 class="tk-ap-col-h">Products</h3>';
      productsHits.forEach(h => {
        const url = '/products/' + escapeAttr(h.handle || '');
        const img = h.image || h.product_image || '';
        const titleHl = (h._highlightResult && h._highlightResult.title && h._highlightResult.title.value) || escapeHtml(h.title || '');
        const price = (h.price != null) ? '$' + Number(h.price).toFixed(2) : '';
        const compare = (h.compare_at_price && h.compare_at_price > h.price)
          ? `<span class="tk-ap-product__price-was">$${Number(h.compare_at_price).toFixed(2)}</span>`
          : '';
        html += `
          <a href="${url}" class="tk-ap-product">
            ${img
              ? `<img class="tk-ap-product__img" src="${escapeAttr(img)}" alt="${escapeAttr(h.title || '')}" loading="lazy">`
              : `<div class="tk-ap-product__img"></div>`}
            <div class="tk-ap-product__info">
              <div class="tk-ap-product__title">${titleHl}</div>
              ${price ? `<div class="tk-ap-product__price">${price}${compare}</div>` : ''}
            </div>
          </a>`;
      });
      html += '</div>';
    }

    if (collectionsHits.length > 0) {
      html += '<div class="tk-ap-col-collections"><h3 class="tk-ap-col-h">Collections</h3>';
      collectionsHits.forEach(c => {
        const url = '/collections/' + escapeAttr(c.handle || '');
        const titleHl = (c._highlightResult && c._highlightResult.title && c._highlightResult.title.value) || escapeHtml(c.title || c.handle || '');
        const count = (c.products_count != null) ? Number(c.products_count).toLocaleString() : '';
        html += `
          <a href="${url}" class="tk-ap-collection">
            <span>${titleHl}</span>
            ${count ? `<span class="tk-ap-collection__count">${count}</span>` : ''}
          </a>`;
      });
      html += '</div>';
    }

    html += '</div>';

    const totalProducts = (products && products.nbHits) || 0;
    if (totalProducts > 0) {
      const searchUrl = '/search?q=' + encodeURIComponent(q) + '&type=product';
      html += `<a href="${escapeAttr(searchUrl)}" class="tk-ap-viewall">View all ${totalProducts.toLocaleString()} result${totalProducts !== 1 ? 's' : ''} for &ldquo;${escapeHtml(q)}&rdquo; &rarr;</a>`;
    }

    panel.innerHTML = html;
    panel.classList.add('is-open');
  }

  function setupAutocomplete(input) {
    // Find a positioned wrapper to attach the panel to
    const wrapper = input.closest('.header__search') || input.closest('form.search') || input.parentElement;
    if (!wrapper) return;

    // The .header__search has position:relative + transform; perfect anchor.
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }

    const panel = document.createElement('div');
    panel.className = 'tk-ap-panel';
    panel.setAttribute('role', 'listbox');
    wrapper.appendChild(panel);

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => doSearch(input.value, panel), DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      const v = (input.value || '').trim();
      if (v.length >= MIN_CHARS && panel.innerHTML) {
        panel.classList.add('is-open');
      }
    });

    document.addEventListener('click', (e) => {
      if (panel.contains(e.target) || e.target === input) return;
      panel.classList.remove('is-open');
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        panel.classList.remove('is-open');
        input.blur();
      }
    });

    input.closest('form')?.addEventListener('submit', () => {
      panel.classList.remove('is-open');
    });
  }

  function init() {
    ensureStyles();
    const inputs = document.querySelectorAll('input[type="search"][name="q"]');
    Array.from(inputs)
      .filter(inp => inp.closest('store-header, .header, header.header, .header__search'))
      .forEach(setupAutocomplete);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
